import {isUndefined} from 'lodash';
import React, {Fragment} from 'react';
import styled, {css} from 'styled-components';

import {CancelToken} from '../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {AccountAvatar} from '../../../components/common/avatars/accountAvatar';
import {DropdownFetcher} from '../../../components/common/dropdowns/source/asyncDropdownSource';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {
  FilterRowGenerator,
  ResourceMultiSelectFilterDropdown,
} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../../components/common/filters/sourceFilter/filterDropdownSource';
import {VirtualTableSortableColumnDirectionsEnum} from '../../../components/common/table/helpers/tableHelpers';
import {batch, useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {
  fetchAccountsAsyncAction,
  fetchAccountsByQueryAsyncAction,
  fetchAccountsFullAction,
} from '../../../store/data/accounts/accountsActions';
import {getAnalyticsFilterAllAccounts} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Types.
 */

interface AccountsFilterPaginationData {
  pageToken?: string;
}

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.ACCOUNTS];
const dropdownAccountIconSize = 20;
const buttonAccountIconSize = 16;
const fetchAccountsLimit = 50;

/*
 * Styles.
 */

const StyledAccountAvatarBase = css`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

const StyledAccountAvatarDropdown = styled(AccountAvatar)`
  ${StyledAccountAvatarBase}
  margin-right: 10px;
`;

const StyledAccountAvatarButton = styled(AccountAvatar)`
  ${StyledAccountAvatarBase}
  margin-right: 4px;
`;

/*
 * Hook.
 */

export const useAnalyticsAccountsFilter = (): AnalyticsFilterItem => {
  const [selectedItemIds, onUpdate] = useAnalyticsFilter(AnalyticsFilterTypesEnum.ACCOUNTS);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.ACCOUNTS);

  const allLoadedAccounts = useAppSelector(getAnalyticsFilterAllAccounts);
  const dispatch = useAppDispatch();

  /*
   * Callbacks.
   */

  const onFilterUpdate = (updatedSelectedItemIds: ReadonlyArray<number>) => {
    // We batch the updating of the selected item ids and adding the accounts
    // to state to ensure they aren't garbage collected as this state is being set.
    batch(() => {
      onUpdate(updatedSelectedItemIds);
      // The fetcher used below does not actually add the accounts to state. Instead, the accounts fetched
      // live strictly in the dropdown component. Therefore, to add the full accounts model for each of
      // the selected ids to our redux state, we fetch them here to ensure they aren't garbage
      // collected with a component re-render.
      //
      // The fetcher could in theory add the accounts to the redux state at the time of fetching,
      // but in order to do so, it would require extra complexity. Specifically, it would require us to
      // keep track of an array of account ids to avoid having them be garbage collected and also
      // clear those same ids when they are no longer needed.
      //
      // So although this approach requires an extra request, it saves a
      // decent bit of code and is generally far easier to reason about.
      dispatch(fetchAccountsAsyncAction(updatedSelectedItemIds));
    });

    trackOnEdit(updatedSelectedItemIds, selectedItemIds);
  };

  /*
   * Fetcher.
   */

  const accountsFetcher: DropdownFetcher<AccountModel, AccountsFilterPaginationData> = async (
    query: string,
    accounts: ReadonlyArray<AccountModel>,
    cancelToken: CancelToken,
    paginationData?: AccountsFilterPaginationData,
  ) => {
    // We only run the query for the accounts if there are at least 3 characters in the query.
    if (query.length > 2) {
      const response = await dispatch(
        fetchAccountsByQueryAsyncAction({
          query,
          limit: fetchAccountsLimit,
          cancelToken,
          pageToken: paginationData?.pageToken,
        }),
      );
      cancelToken.throwIfRequested();

      return {
        items: accounts.concat(response.accounts),
        canLoadMore: Boolean(response.pageToken),
        paginationData: {
          pageToken: response.pageToken,
        },
      };
    }

    // Otherwise, fetch all the accounts, sorted alphabetically.
    const response = await dispatch(
      fetchAccountsFullAction({
        sortBy: 'name',
        sortOrder: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
        limit: fetchAccountsLimit,
        cancelToken,
        pageToken: paginationData?.pageToken,
      }),
    );
    cancelToken.throwIfRequested();

    return {
      items: accounts.concat(response.accounts),
      canLoadMore: !isUndefined(response.next),
      paginationData: {
        pageToken: response.next,
      },
    };
  };

  const dropdownSource = computeFilterDropdownSourceAsync(accountsFetcher);

  /*
   * Renderers.
   */

  const isActive = !isUndefined(selectedItemIds) && selectedItemIds.length > 0;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedItemIds}
      itemsMap={allLoadedAccounts}
      iconName={isActive ? activeIconName : iconName}
      label={name}
      renderItem={renderButtonItem}
      isActive={isActive}
    />
  );

  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ResourceMultiSelectFilterDropdown
      {...dropdownProps}
      dropdownTitle={name}
      selectedItemIds={selectedItemIds}
      onSetItems={onFilterUpdate}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={trackOnRemove}
    />
  );

  return {
    id: 'accounts-filter',
    type: AnalyticsFilterTypesEnum.ACCOUNTS,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const computeFilterGenerator: FilterRowGenerator<AccountModel> = (
  item,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isChecked = dropdownSelectedIds.includes(item.id);

  return {
    renderer: () => (
      <DropdownItemContent isChecked={isChecked}>
        <StyledAccountAvatarDropdown
          account={item}
          size={dropdownAccountIconSize}
          avatarIconSize={dropdownAccountIconSize}
        />
        {item.name}
      </DropdownItemContent>
    ),
    onSelect: makeOnItemSelect(item.id),
    isSelected: isChecked,
  };
};

const renderButtonItem = (account: AccountModel) => (
  <Fragment key={account.id}>
    <StyledAccountAvatarButton
      account={account}
      size={buttonAccountIconSize}
      avatarIconSize={buttonAccountIconSize}
    />
    {account.name}
  </Fragment>
);
